/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import { compose, withHandlers } from 'recompose'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'
import { reduxForm, initialize, change, getFormValues } from 'redux-form'
import _get from 'lodash/get'
import _omit from 'lodash/omit'
import { analytics, SITE_SECTIONS } from 'services/Analytics'
import { ERROR_CODES } from 'constants/errors'
import { ALERTS } from 'constants/alerts'
import { EMPTY_OBJECT } from 'constants/frozenObjects'
import { withLocalization } from 'hocs/localization'
import { formWithAddress, FORM_COUNTRY_TYPES } from 'hocs/formWithAddress'
import { onSaveShippingAddress, onCompleteSavingShippingAddress } from 'redux-app/actionCreators/subscription'
import { onShowError, createAlert } from 'redux-app/modules/alerts'
import { openModal } from 'redux-app/modules/modals'
import { getUserDetails, isUserUnableToUpdateInfo } from 'redux-app/selectors/user'
import { correctedShippingAddressModal } from 'apps/Profile/CorrectedAddressModal'
import { getErrorCodes } from 'utils/error'
import { removeDashes } from 'utils/form'
import { getPrintedPostalCode } from 'utils/printedData'
import ShippingInfoForm from './ShippingInfoForm'

const FORM_NAME = 'profileShippingForm'
const BILLING_FORM_NAME = 'profileBillingForm'

const getInitialValues = createSelector(
  state => _get(getUserDetails(state), 'shippingAddress', EMPTY_OBJECT),
  shippingAddress => ({
    ...shippingAddress,
    postalCode: getPrintedPostalCode(_get(shippingAddress, 'postalCode')),
    isCustomerFromExcludedState: shippingAddress?.stateId == 1 || shippingAddress?.stateId == 10 //Alaska or Hawaii
  }),
)

export default compose(
  connect(
    state => ({
      initialValues: getInitialValues(state),
      isUserUnableToUpdateInfo: isUserUnableToUpdateInfo(state),
      postalCode: _get(getFormValues(FORM_NAME)(state), 'postalCode', ''),
      isCustomerFromExcludedState: _get(getFormValues(FORM_NAME)(state), 'isCustomerFromExcludedState', false),
    }),
    {
      onShowError,
      createAlert,
      openModal,
      initialize,
      change,
      onSaveShippingAddress,
      onCompleteSavingShippingAddress,
    },
  ),
  withLocalization('company.paymentInfo.billing'),
  withHandlers({
    onUpdateField: ({ change }) => ({ name, value }) => {
      change(BILLING_FORM_NAME, name, value)
    },
    onSubmit: ({
      onSaveShippingAddress,
      onShowError,
      createAlert,
      openModal,
      onCompleteSavingShippingAddress,
    }) => ({ postalCode, ...restFormValues }) => {
      const formValues = {
        ...restFormValues,
        postalCode: removeDashes(postalCode),
      }
      return onSaveShippingAddress(formValues)
        .then(({ value: { result } }) => {
          if (!result.isMatch) {
            const data = {
              shippingCorrectedResult: _omit(result, ['isMatch']),
            }
            openModal({ name: correctedShippingAddressModal.name, data })
            return
          }
          createAlert({ name: ALERTS.successBillingInfoChanged })
          analytics.saveChanges({ sectionName: SITE_SECTIONS.shippingAddress })
          return onCompleteSavingShippingAddress()
        })
        .catch((error: Error) => {
          analytics.showMyAccountError({
            errorCodes: getErrorCodes(error),
            sectionType: SITE_SECTIONS.shippingAddress,
          })
          if (getErrorCodes(error).includes(ERROR_CODES.addressIsNotValid)) {
            createAlert({ name: ALERTS.shippingAddressIsNotValid })
          } else {
            onShowError(error, formValues)
          }
        })
    },
    onConfirmCorrectedAddress: ({
      onSaveShippingAddress,
      onShowError,
      createAlert,
      onCompleteSavingShippingAddress,
      initialize,
    }) => ({ shippingCorrectedResult }) => {
      return onSaveShippingAddress(shippingCorrectedResult)
        .then(({ value: { result } }) => {
          if (!result.isMatch) {
            createAlert({ name: ALERTS.profileShippingFormError })
            return
          }
          initialize(FORM_NAME, {
            ...result,
            postalCode: getPrintedPostalCode(result.postalCode),
          })
          createAlert({ name: ALERTS.successBillingInfoChanged })
          analytics.saveChanges({ sectionName: SITE_SECTIONS.shippingAddress })
          return onCompleteSavingShippingAddress()
        })
        .catch(error => {
          analytics.showMyAccountError({
            errorCodes: getErrorCodes(error),
            sectionType: SITE_SECTIONS.shippingAddress,
          })
          onShowError(error)
        })
    },
  }),
  reduxForm({
    form: FORM_NAME,
  }),
  formWithAddress(FORM_COUNTRY_TYPES.billing),
)(ShippingInfoForm)
